import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CAPABILITIES, CASE_STUDIES, PARTNERS, STATS } from "@/lib/data";

export default function Home() {
  const featured = CASE_STUDIES;

  return (
    <div data-testid="home-page" className="bg-bone">
      {/* ---------------- HERO ---------------- */}
      <section className="relative pt-16 md:pt-24 lg:pt-32 pb-24 md:pb-32">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="overline text-vermilion mb-8">
                  <span className="inline-block w-6 h-px bg-vermilion align-middle mr-3" />
                  A Global Capability Center, re-imagined
                </div>
                <h1 className="font-serif font-medium text-[44px] sm:text-6xl lg:text-[88px] xl:text-[104px] leading-[0.95] tracking-[-0.02em]">
                  Engineering the
                  <br />
                  products that
                  <br />
                  <span className="italic text-ink/70">don’t yet exist.</span>
                </h1>
              </motion.div>
            </div>

            <motion.div
              className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-base md:text-lg text-ink/70 leading-relaxed">
                ThinkRight Tech is the GCC behind venture-backed product companies
                like <span className="text-ink font-medium">Matchbook.ai</span>. We build
                cutting-edge AI, data and product software for founders who refuse to
                choose between speed and rigor.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-partner"
                  className="btn-primary"
                >
                  Partner with us <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/work" data-testid="hero-cta-work" className="btn-outline">
                  See our work
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero imagery — editorial split */}
        <div className="tr-container mt-20 md:mt-28">
          <Reveal className="grid grid-cols-12 gap-6 md:gap-8">
            <div className="col-span-12 md:col-span-8 aspect-[16/9] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/15548727/pexels-photo-15548727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Modern corporate architecture"
                className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.03]"
              />
            </div>
            <div className="col-span-12 md:col-span-4 aspect-[3/4] md:aspect-auto overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1636142426623-98a21fe2fb8f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3NzY3MDY2MTN8MA&ixlib=rb-4.1.0&q=85"
                alt="Corporate glass reflection"
                className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.03]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- PARTNERS MARQUEE ---------------- */}
      <section className="py-12 border-y border-border bg-bone">
        <div className="overline text-center text-ink/50 mb-8">
          Trusted by product companies building the future
        </div>
        <div className="marquee-mask overflow-hidden">
          <div className="flex gap-20 animate-marquee whitespace-nowrap w-max">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className="font-serif text-3xl md:text-4xl text-ink/60"
                data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z]/g, "-")}-${i}`}
              >
                {p.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- STATS ---------------- */}
      <section className="py-24 md:py-32">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <div className="overline text-vermilion mb-6">By the numbers</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Embedded teams.
                <br />
                Product outcomes.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8 grid grid-cols-2 gap-0 border-t border-border">
              {STATS.map((s, i) => (
                <Reveal
                  key={s.label}
                  delay={i}
                  className={`p-8 md:p-10 border-b border-border ${
                    i % 2 === 0 ? "md:border-r border-border" : ""
                  }`}
                >
                  <div className="display-num text-5xl md:text-7xl text-ink">
                    {s.value}
                  </div>
                  <div className="mt-4 text-sm text-ink/60 leading-relaxed max-w-[28ch]">
                    {s.label}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED WORK ---------------- */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="tr-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="overline text-vermilion mb-6">Selected work</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
                We build the product.
                <br />
                <span className="italic text-ink/60">The founders take the bow.</span>
              </h2>
            </div>
            <Link
              to="/work"
              data-testid="home-all-work"
              className="inline-flex items-center gap-2 link-underline text-sm"
            >
              All case studies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-12 gap-8 md:gap-12">
            {featured.map((cs, i) => (
              <Reveal
                key={cs.slug}
                delay={i}
                className={`col-span-12 ${i === 0 ? "md:col-span-7" : "md:col-span-5 md:mt-24"}`}
              >
                <Link
                  to={`/work/${cs.slug}`}
                  data-testid={`featured-work-${cs.slug}`}
                  className="block group"
                >
                  <div className="aspect-[4/3] overflow-hidden mb-6 bg-muted">
                    <img
                      src={cs.hero_image}
                      alt={cs.name}
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-ink/50 mb-3">
                    <span>{cs.sector}</span>
                    <span>{cs.year}</span>
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                    {cs.name}
                  </h3>
                  <p className="mt-4 text-ink/70 text-base md:text-lg max-w-xl leading-relaxed">
                    {cs.tagline}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm group-hover:text-vermilion transition-colors">
                    Read the case study <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CAPABILITIES PREVIEW ---------------- */}
      <section className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-vermilion mb-6">Capabilities</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Six disciplines.
                <br />
                <span className="italic text-white/60">One standing team.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7">
              <p className="text-lg text-white/70 leading-relaxed">
                ThinkRight is organized like a product company, not a service vendor.
                Applied AI, ML engineering, data, product, research and platform —
                integrated, on-call, and accountable to the same outcomes as your
                founders.
              </p>
              <Link
                to="/services"
                data-testid="home-capabilities-cta"
                className="inline-flex items-center gap-2 mt-8 border border-white/40 px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
              >
                Explore capabilities <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-12 border-t border-white/15">
            {CAPABILITIES.map((c, i) => (
              <Reveal
                key={c.id}
                delay={i}
                className={`col-span-12 md:col-span-6 lg:col-span-4 border-b border-white/15 ${
                  i % 3 !== 2 ? "lg:border-r" : ""
                } ${i % 2 !== 1 ? "md:border-r lg:border-r" : ""} p-8 md:p-10 group hover:bg-white/5 transition-colors`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-mono text-xs text-white/50">{c.number}</span>
                  <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-vermilion transition-colors" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                  {c.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">{c.blurb}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TALENT PULL ---------------- */}
      <section className="py-24 md:py-32 border-t border-border">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-7">
              <div className="overline text-vermilion mb-6">For builders</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                The kind of work
                <br />
                you wish you could show
                <br />
                at a conference.
              </h2>
              <p className="mt-8 max-w-xl text-lg text-ink/70 leading-relaxed">
                We hire a small number of senior engineers, researchers and designers
                each year. If you want to build AI that ships to real users — and sit
                in the room where the decisions are made — we should talk.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/careers" data-testid="home-careers-cta" className="btn-accent">
                  See open roles <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/life" data-testid="home-life-cta" className="btn-outline">
                  Life at ThinkRight
                </Link>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/6804071/pexels-photo-6804071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Diverse engineering team"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.03]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
